/**
 * The KPI table (PRD §5.4).
 *
 * "The single most important field group in the product" — so it is a real
 * editable table rather than a list of forms. A metric has a baseline, a target
 * and an actual, and the point of the group is *comparison*: whether the actual
 * has cleared the target, and by how much against where it started.
 *
 * Three decisions follow from that:
 *
 * **The whole table saves at once.** Rows are added, reordered and removed
 * locally and one Save sends the set. That matches the API, and it matches how
 * people edit a table — nobody expects each cell to commit on blur.
 *
 * **Progress is shown as a bar AND as text.** A bar alone conveys the
 * comparison visually only, which PRD §9.7 does not permit; the figures sit
 * beside it in tabular numerals so columns line up and can be scanned.
 *
 * **An unmeasured KPI says so.** A blank actual is the normal state for a pilot
 * that has not run yet, and rendering it as an empty cell makes it
 * indistinguishable from a rendering failure.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.2, PRD §5.4, §9.7
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PilotKpi } from '@nexus/contracts';

import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHead } from '../../../components/ui/Card';
import { Empty } from '../../../components/ui/Empty';
import { Input } from '../../../components/ui/Input';
import { Kicker } from '../../../components/ui/Kicker';
import { Meter } from '../../../components/ui/Meter';
import { cn } from '../../../lib/cn';
import { formatNumber } from '../../../lib/format';

import type { KpiDraft } from './kpi-math';
import { emptyDraft, progressPercent, toDraft } from './kpi-math';

export interface KpiTableProps {
  kpis: readonly PilotKpi[];
  canEdit: boolean;
  /** Saves the whole set. Rejection is reported by the caller. */
  onSave: (drafts: readonly KpiDraft[]) => void;
  isSaving: boolean;
}

export function KpiTable({
  kpis,
  canEdit,
  onSave,
  isSaving,
}: KpiTableProps): React.JSX.Element {
  const { t } = useTranslation();
  const [drafts, setDrafts] = useState<KpiDraft[] | null>(null);

  /* Null means "not editing": the saved rows are shown read-only until someone
     starts. Deriving the drafts from props on every render instead would
     discard a half-finished edit whenever the pilot refetched. */
  const editing = drafts !== null;

  /* Takes the current drafts as an argument rather than reading through a
     `null` guard: every caller is inside the editing branch, where they cannot
     be null, and a guard there would be an unreachable branch pretending to be
     defensive. */
  const update = (
    current: readonly KpiDraft[],
    index: number,
    patch: Partial<KpiDraft>,
  ): void => {
    setDrafts(current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  return (
    <Card>
      <CardHead
        actions={
          canEdit ? (
            editing ? (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setDrafts(null);
                  }}
                >
                  {t('actions.cancel')}
                </Button>
                <Button
                  size="sm"
                  disabled={isSaving}
                  onClick={() => {
                    onSave(drafts);
                    setDrafts(null);
                  }}
                >
                  {isSaving ? t('pilot.kpis.saving') : t('pilot.kpis.save')}
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setDrafts(kpis.map(toDraft));
                }}
              >
                {t('pilot.kpis.edit')}
              </Button>
            )
          ) : undefined
        }
      >
        <Kicker as="h2" count={kpis.length}>
          {t('pilot.kpis.title')}
        </Kicker>
      </CardHead>

      <CardBody flush>
        {editing ? (
          <div className="p-s3">
            <ul className="gap-s3 flex flex-col">
              {drafts.map((draft, index) => (
                <li key={draft.key} className="gap-s2 grid grid-cols-2 md:grid-cols-5">
                  <label className="col-span-2 md:col-span-1">
                    <span className={FIELD_LABEL}>{t('pilot.kpis.metric')}</span>
                    <Input
                      value={draft.metricName}
                      onChange={(event) => {
                        update(drafts, index, { metricName: event.target.value });
                      }}
                    />
                  </label>
                  <label>
                    <span className={FIELD_LABEL}>{t('pilot.kpis.unit')}</span>
                    <Input
                      value={draft.unit}
                      onChange={(event) => {
                        update(drafts, index, { unit: event.target.value });
                      }}
                    />
                  </label>
                  <label>
                    <span className={FIELD_LABEL}>{t('pilot.kpis.baseline')}</span>
                    <Input
                      inputMode="decimal"
                      value={draft.baseline}
                      onChange={(event) => {
                        update(drafts, index, { baseline: event.target.value });
                      }}
                    />
                  </label>
                  <label>
                    <span className={FIELD_LABEL}>{t('pilot.kpis.target')}</span>
                    <Input
                      inputMode="decimal"
                      value={draft.target}
                      onChange={(event) => {
                        update(drafts, index, { target: event.target.value });
                      }}
                    />
                  </label>
                  <label>
                    <span className={FIELD_LABEL}>{t('pilot.kpis.actual')}</span>
                    <Input
                      inputMode="decimal"
                      value={draft.actual}
                      onChange={(event) => {
                        update(drafts, index, { actual: event.target.value });
                      }}
                    />
                  </label>
                </li>
              ))}
            </ul>

            <div className="gap-s2 mt-s3 flex">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  /* `drafts` is non-null here: the button only renders inside
                     the editing branch. Reading it directly rather than through
                     a `?? []` fallback keeps that guarantee visible instead of
                     implying the state could be absent. */
                  setDrafts([...drafts, emptyDraft()]);
                }}
              >
                {t('pilot.kpis.add')}
              </Button>
              {drafts.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDrafts(drafts.slice(0, -1));
                  }}
                >
                  {t('pilot.kpis.removeLast')}
                </Button>
              )}
            </div>
          </div>
        ) : kpis.length === 0 ? (
          <Empty title={t('pilot.kpis.emptyTitle')}>{t('pilot.kpis.emptyBody')}</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">{t('pilot.kpis.caption')}</caption>
              <thead>
                <tr className="border-gray-100 border-b">
                  <th scope="col" className={cn(TH, 'text-left')}>
                    {t('pilot.kpis.metric')}
                  </th>
                  <th scope="col" className={cn(TH, 'text-right')}>
                    {t('pilot.kpis.baseline')}
                  </th>
                  <th scope="col" className={cn(TH, 'text-right')}>
                    {t('pilot.kpis.target')}
                  </th>
                  <th scope="col" className={cn(TH, 'text-right')}>
                    {t('pilot.kpis.actual')}
                  </th>
                  <th scope="col" className={cn(TH, 'text-left')}>
                    {t('pilot.kpis.progress')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {kpis.map((kpi) => {
                  const percent = progressPercent(kpi);

                  return (
                    <tr key={kpi.id} className="border-gray-100 border-b last:border-b-0">
                      <td className="px-s3 py-2.5">
                        <span className="font-display text-navy-text font-bold">
                          {kpi.metricName}
                        </span>
                        {kpi.unit === null ? null : (
                          <span className="text-body ml-1.5 text-xs font-light">
                            {kpi.unit}
                          </span>
                        )}
                      </td>
                      <td className={CELL_NUM}>{figure(kpi.baseline, t)}</td>
                      <td className={CELL_NUM}>{figure(kpi.target, t)}</td>
                      <td className={cn(CELL_NUM, 'text-navy-text font-semibold')}>
                        {figure(kpi.actual, t)}
                      </td>
                      <td className="px-s3 w-[200px] py-2.5">
                        {percent === null ? (
                          <span className="text-body text-xs font-light">
                            {t('pilot.kpis.noProgress')}
                          </span>
                        ) : (
                          <Meter
                            value={percent}
                            max={100}
                            size="full"
                            label={t('pilot.kpis.progressOf', { metric: kpi.metricName })}
                            display={t('pilot.kpis.percent', {
                              value: formatNumber(Math.round(percent)),
                            })}
                            /* Green once the target is met — the value IS the
                               data (§4.2), and this is the one fact the table
                               exists to communicate. */
                            fillClassName={percent >= 100 ? 'bg-green-dark' : 'bg-blue'}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

const TH =
  'text-body font-display px-s3 py-2.5 text-[10px] font-extrabold tracking-[0.12em] whitespace-nowrap uppercase';

const CELL_NUM = 'text-body px-s3 py-2.5 text-right tabular-nums';

const FIELD_LABEL =
  'mb-1 block font-display text-[10px] font-extrabold tracking-[0.1em] text-body uppercase';

/** A figure, or an explicit marker when it was never recorded. */
function figure(value: number | null, t: (key: string) => string): React.ReactNode {
  if (value === null) {
    return <span className="text-gray-400">{t('pilot.kpis.notRecorded')}</span>;
  }

  return formatNumber(value, { maximumFractionDigits: 6 });
}
